import EmptyState from "@/components/empty-state/EmptyState.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import {
  ConfirmationModal,
  Page,
  useModals,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Table,
  TableCell,
} from "@skailer-com/uikit";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { WorkPatternEditModal } from "@/pages/settings/work-patterns/widgets/WorkPatternEditModal.tsx";
import { useWorkPatternDelete, useWorkPatterns } from "@/queries/use-work-patterns.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiWorkPattern } from "@/types/work-pattern.types.ts";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

export function WorkPatterns() {
  const { t } = useTranslation();
  const { openModal } = useModals();

  useTitle(t("core:work_patterns"), t("common:settings"));

  const { data: workPatterns, isLoading, isFetching } = useWorkPatterns();
  const workPatternDelete = useWorkPatternDelete();

  const columns = useMemo<ColumnDef<ApiWorkPattern>[]>(() => {
    return [
      {
        header: t("common:title"),
        cell: (row) => <TableCell>{row.row.original.title}</TableCell>,
        accessorKey: "title",
        ...ColumnSizes.standard,
      },
      {
        header: t("core:employees"),
        cell: (row) => <div className={"text-left px-2"}>{row.row.original.employees_count}</div>,
        accessorKey: "employees_count",
        ...ColumnSizes.narrow,
      },
    ];
  }, [workPatterns]);

  const handleEditOrCreate = useCallback((workPattern: ApiWorkPattern | undefined = undefined) => {
    openModal(WorkPatternEditModal, {
      workPattern: workPattern,
    });
  }, []);

  const handleDelete = useCallback(async (workPattern: ApiWorkPattern) => {
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          await workPatternDelete.mutateAsync(workPattern.id, {
            onSuccess() {
              analyticsService.trackEvent(analyticEvents.workPatterns.deleted, {
                [analyticProperties.id]: workPattern.id,
              });
            },
          });
        } catch {}
      },
    });
  }, []);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.workPatterns.viewed);
  });

  const renderRowAction = useCallback(
    (row: Row<ApiWorkPattern>) => (
      <DropdownMenu>
        <DropdownMenuItem
          onClick={() => {
            handleEditOrCreate(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen"} />}
          title={t("common:edit")}
        />
        <DropdownMenuItem
          onClick={() => {
            void handleDelete(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
          variant={"menuDanger"}
          title={t("common:delete") + "..."}
        />
      </DropdownMenu>
    ),
    [handleDelete, handleEditOrCreate, t]
  );

  return (
    <Page>
      <Page.Header
        showBack={true}
        title={t("core:work_patterns")}
        navigateTo={"/settings/employment"}
      >
        <Stack>
          <Button
            onClick={() => handleEditOrCreate()}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
          >
            {t("common:create_new")}
          </Button>
        </Stack>
      </Page.Header>
      <Page.Content>
        <Table
          renderRowAction={renderRowAction}
          data={workPatterns || []}
          columns={columns}
          isFetching={isFetching || workPatternDelete.isPending}
          showSkeleton={isLoading}
          notFoundComponent={<EmptyStateNothingFound />}
          emptyStateComponent={<EmptyState />}
        />
      </Page.Content>
    </Page>
  );
}
